import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {MeshCellValueFormat} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/MeshCellValueFormat';
import {IMeshCellValueFormat} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/ValueFormat';
import {MeshCellValueFormatKind} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/ValueFormatKind';
import {
    MeshCellBorderSide,
    MeshCellBorderType
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellSettings/MeshCellBorder';
import {MeshCellTextWrapping} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellSettings/MeshCellTextAlign';
import {
    IMeshCellSettings,
    MeshCellColor,
    MeshCellSettingsProps
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellSettings';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshRange';
import {MeshSheetSize} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshSheetSize';
import {MeshState} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshState';
import {Slot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/Slot';
import {EMPTY_SLOT_FORMULA, SlotFormula} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotTypes';
import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {
    CellSettingsBarBorderAction,
    CellSettingsBarTmpValue
} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/CellSettingsBarState';
import {MeshUtils} from '@mintdata-wsp/common/util/MeshUtils';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {action} from 'mobx';

const ALL_BORDERS:MeshCellBorderSide[] =
    [
        MeshCellBorderSide.Top,
        MeshCellBorderSide.Right,
        MeshCellBorderSide.Bottom,
        MeshCellBorderSide.Left
    ];

const LAZY_PROPS:Array<MeshCellSettingsProps> =
    [
        MeshCellSettingsProps.FontName,
        MeshCellSettingsProps.FontSize,
        MeshCellSettingsProps.TextColor,
        MeshCellSettingsProps.BgColor
    ];

const RECALC_ROW_HEIGHT_PROPS:Array<MeshCellSettingsProps> =
    [
        MeshCellSettingsProps.FontName,
        MeshCellSettingsProps.FontSize,
        MeshCellSettingsProps.TextBold,
        MeshCellSettingsProps.WrapText
    ];

export class MeshCellSettingsController
{
    private _calcRowHeight(ctx:IWSPMeshContext, prop:MeshCellSettingsProps)
    {
        const isNeedRecalcRowHeight:boolean = RECALC_ROW_HEIGHT_PROPS.indexOf(prop) !== -1;

        if( isNeedRecalcRowHeight )
            controllers.ui.spreadsheets.mesh.calcRowHeight(ctx);
    }

    @action
    public toggleProperty<T extends keyof IMeshCellSettings>(ctx:IWSPMeshContext, prop:T):void
    {
        const state = this._getState(ctx);
        if( !state ) return;

        const {sheetSettingsCtrl, sheetId} = ctx;
        const {meshState, numCols, numRows, activeCellSettings} = state;

        let newValue:any = true;

        if( activeCellSettings )
            newValue = activeCellSettings.getProperty(prop) ? null : true;

        MeshUtils.eachCellInRange(meshState.details.selectedRange, numRows, numCols, (cellKey:string) =>
        {
            const cellSettings = sheetSettingsCtrl.getOrCreateCellSettings(sheetId, cellKey);
            cellSettings.setProperty(prop, newValue);
        });

        this._calcRowHeight(ctx, <MeshCellSettingsProps>(prop as any));
    }

    @action
    public setProperty<T extends keyof IMeshCellSettings>(ctx:IWSPMeshContext, prop:T, value:IMeshCellSettings[T]):void
    {
        const state = this._getState(ctx);
        if( !state ) return;

        const {sheetSettingsCtrl, sheetId} = ctx;
        const {isMenuOpened} = rootState.ui.visualDesigner.workbench.cellSettingsBar;

        const isLazyProp:boolean = LAZY_PROPS.indexOf(<MeshCellSettingsProps>(prop as any)) !== -1;

        if( isLazyProp && isMenuOpened )
        {
            controllers.ui.spreadsheets.cellSettingsBar.setTmpValue(value as CellSettingsBarTmpValue);
            return;
        }

        const {meshState, numCols, numRows} = state;

        MeshUtils.eachCellInRange(meshState.details.selectedRange, numRows, numCols, (cellKey:string) =>
        {
            const cellSettings = sheetSettingsCtrl.getOrCreateCellSettings(sheetId, cellKey);

            cellSettings.setProperty(prop, value);
        });

        if( isLazyProp )
            controllers.ui.spreadsheets.cellSettingsBar.setTmpValue(null);

        this._calcRowHeight(ctx, <MeshCellSettingsProps>(prop as any));
    }

    @action
    public toggleTextWrappingClip(ctx:IWSPMeshContext):void
    {
        const state = this._getState(ctx);

        let oldValue:MeshCellTextWrapping;

        if( state && state.activeCellSettings )
            oldValue = state.activeCellSettings.getProperty(MeshCellSettingsProps.WrapText as any) as MeshCellTextWrapping;

        if( !oldValue || oldValue !== MeshCellTextWrapping.Clip )
            this.setProperty(ctx, MeshCellSettingsProps.WrapText, MeshCellTextWrapping.Clip);
        else
            this.setProperty(ctx, MeshCellSettingsProps.WrapText, MeshCellTextWrapping.Default);
    }

    @action
    public maskValue(ctx:IWSPMeshContext):void
    {
        const state = this._getState(ctx);
        if( !state ) return;

        const {sheetSettingsCtrl, sheetId} = ctx;
        const {meshState, numCols, numRows, activeCellSettings} = state;

        let newIsValueMasked = true;

        if( activeCellSettings )
            newIsValueMasked = !activeCellSettings.isValueMasked;

        MeshUtils.eachCellInRange(meshState.details.selectedRange, numRows, numCols, (cellKey:string) =>
        {
            const cellSettings = sheetSettingsCtrl.getOrCreateCellSettings(sheetId, cellKey);
            cellSettings.setIsValueMasked(newIsValueMasked);
        });
    }

    @action
    public setValueFormat(ctx:IWSPMeshContext, valueFormat:IMeshCellValueFormat):void
    {
        const state = this._getState(ctx);
        if( !state ) return;

        const {sheetSettingsCtrl, sheetId} = ctx;
        const {meshState} = state;
        let selectedRange = meshState.details.selectedRange;
        sheetSettingsCtrl.setCellFormat(sheetId, selectedRange, state.meshState.size, valueFormat.id);
    }

    @action
    public setDigitsAfterPoint(ctx:IWSPMeshContext, delta:number):void
    {
        const state = this._getState(ctx);
        if( !state ) return;

        const {sheetSettingsCtrl, sheetId} = ctx;
        const {meshState, numCols, numRows} = state;

        MeshUtils.eachCellInRange(meshState.details.selectedRange, numRows, numCols, (cellKey:string) =>
        {
            const cellFormat = sheetSettingsCtrl.getOrCreateCellFormat(sheetId, cellKey);
            const sheetController = controllers.ui.spreadsheets.sheet;
            const slot:Slot = sheetController.findSlot(sheetId, SlotLocation.fromKey(cellKey));

            let newFormatId = this._getNewFormatId(
                cellFormat,
                delta,
                slot ? slot.formula : EMPTY_SLOT_FORMULA
            );

            sheetSettingsCtrl.setCellFormat(
                sheetId,
                new MeshRange(MeshLocation.fromKey(cellKey), MeshLocation.fromKey(cellKey)),
                MeshSheetSize.singleCell(),
                newFormatId
            );
        });
    }

    private _getNewFormatId(cellFormat:IMeshCellValueFormat, delta:number, formula:SlotFormula):string
    {
        let arrayToUse;
        const kind = cellFormat.kind;
        if( kind == MeshCellValueFormatKind.Number )
        {
            arrayToUse = MeshCellValueFormat.BuiltInFormats.numberFormats;
        }
        else if( kind == MeshCellValueFormatKind.Percent )
        {
            arrayToUse = MeshCellValueFormat.BuiltInFormats.percentFormats;
        }
        else if( kind == MeshCellValueFormatKind.Currency )
        {
            arrayToUse = MeshCellValueFormat.BuiltInFormats.currencyFormats;
        }
        else if( kind == MeshCellValueFormatKind.Financial )
        {
            arrayToUse = MeshCellValueFormat.BuiltInFormats.financialFormats;
        }
        else if( kind == MeshCellValueFormatKind.Accounting )
        {
            arrayToUse = MeshCellValueFormat.BuiltInFormats.accountingFormats;
        }
        else if( kind == MeshCellValueFormatKind.General && MeshCellValueFormat.Number.isStringOfThisFormat(formula.toString()) )
        {
            arrayToUse = MeshCellValueFormat.BuiltInFormats.numberFormats;
        }
        else
        {
            return cellFormat.id;
        }
        const index = cellFormat.digitsAfterPoint() + delta;
        let newFormatId;
        if( index <= 0 )
        {
            newFormatId = arrayToUse[0];
        }
        else if( index >= 10 )
        {
            newFormatId = arrayToUse[10];
        }
        else
        {
            newFormatId = arrayToUse[index];
        }
        return newFormatId;
    }

    @action
    public setBorder(ctx:IWSPMeshContext, action:CellSettingsBarBorderAction):void
    {
        const state = this._getState(ctx);
        if( !state ) return;

        const {meshState} = state;
        const {borderColor, isBorderTypeDouble} = rootState.ui.visualDesigner.workbench.cellSettingsBar;

        let type:MeshCellBorderType = isBorderTypeDouble ? MeshCellBorderType.Double : MeshCellBorderType.Thin;

        const {from, to} = this._getNormalizedRange(meshState.details.selectedRange, meshState);

        let colFrom, rowFrom, colTo, rowTo;

        let resetCol = 0, resetRow = 0, resetSide;

        let borderSide:MeshCellBorderSide;

        switch( action )
        {
            case CellSettingsBarBorderAction.Top:
                colFrom = from.colIndex;
                colTo = to.colIndex;
                rowFrom = from.rowIndex;
                rowTo = from.rowIndex;
                borderSide = MeshCellBorderSide.Top;
                resetSide = MeshCellBorderSide.Bottom;
                resetRow = -1;
                break;

            case CellSettingsBarBorderAction.Bottom:
                colFrom = from.colIndex;
                colTo = to.colIndex;
                rowFrom = to.rowIndex;
                rowTo = to.rowIndex;
                borderSide = MeshCellBorderSide.Bottom;
                resetSide = MeshCellBorderSide.Top;
                resetRow = +1;
                break;

            case CellSettingsBarBorderAction.Left:
                colFrom = from.colIndex;
                colTo = from.colIndex;
                rowFrom = from.rowIndex;
                rowTo = to.rowIndex;
                borderSide = MeshCellBorderSide.Left;
                resetSide = MeshCellBorderSide.Right;
                resetCol = -1;
                break;

            case CellSettingsBarBorderAction.Right:
                colFrom = to.colIndex;
                colTo = to.colIndex;
                rowFrom = from.rowIndex;
                rowTo = to.rowIndex;
                borderSide = MeshCellBorderSide.Right;
                resetSide = MeshCellBorderSide.Left;
                resetCol = +1;
                break;

            case CellSettingsBarBorderAction.All:
            case CellSettingsBarBorderAction.None:
                this._setAllBorders(ctx, action === CellSettingsBarBorderAction.None);
                return;

            case CellSettingsBarBorderAction.Inside:
                this._setInnerHorBorders(ctx);
                this._setInnerVertBorders(ctx);
                return;

            case CellSettingsBarBorderAction.Horizontal:
                this._setInnerHorBorders(ctx);
                return;

            case CellSettingsBarBorderAction.Vertical:
                this._setInnerVertBorders(ctx);
                return;

            case CellSettingsBarBorderAction.Outside:
                this.setBorder(ctx, CellSettingsBarBorderAction.Top);
                this.setBorder(ctx, CellSettingsBarBorderAction.Left);
                this.setBorder(ctx, CellSettingsBarBorderAction.Right);
                this.setBorder(ctx, CellSettingsBarBorderAction.Bottom);
                return;
        }

        const setBordersRange = this._makeRange(colFrom, rowFrom, colTo, rowTo);

        this._setBorders(ctx, setBordersRange, [borderSide], type, borderColor);

        const resetBordersRange = this._makeRange(
            colFrom + resetCol, rowFrom + resetRow, colTo + resetCol, rowTo + resetRow
        );

        this._setBorders(ctx, resetBordersRange, [resetSide], MeshCellBorderType.Grid);
    }

    private _setInnerHorBorders(ctx:IWSPMeshContext):void
    {
        const state = this._getState(ctx);
        if( !state ) return;

        const {meshState} = state;
        const {borderColor, isBorderTypeDouble} = rootState.ui.visualDesigner.workbench.cellSettingsBar;

        let type:MeshCellBorderType = isBorderTypeDouble ? MeshCellBorderType.Double : MeshCellBorderType.Thin;

        let {selectedRange} = meshState.details;

        selectedRange = this._getNormalizedRange(selectedRange, meshState);

        let colFrom = selectedRange.from.colIndex;
        let rowFrom = selectedRange.from.rowIndex;
        let colTo = selectedRange.to.colIndex;
        let rowTo = selectedRange.to.rowIndex;

        const numRows = rowTo - rowFrom + 1;

        if( numRows < 2 ) return;

        const firstRowInRange = this._makeRange(colFrom, rowFrom, colTo, rowFrom);
        this._setBorders(ctx, firstRowInRange, [MeshCellBorderSide.Bottom], type, borderColor);

        const lastRowInRange = this._makeRange(colFrom, rowTo, colTo, rowTo);
        this._setBorders(ctx, lastRowInRange, [MeshCellBorderSide.Top], type, borderColor);

        if( numRows < 3 ) return;

        const centerRowsInRange = this._makeRange(colFrom, rowFrom + 1, colTo, rowTo - 1);
        this._setBorders(ctx, centerRowsInRange, [MeshCellBorderSide.Top, MeshCellBorderSide.Bottom], type, borderColor);
    }

    private _setInnerVertBorders(ctx:IWSPMeshContext):void
    {
        const state = this._getState(ctx);
        if( !state ) return;

        const {meshState} = state;
        const {borderColor, isBorderTypeDouble} = rootState.ui.visualDesigner.workbench.cellSettingsBar;

        let type:MeshCellBorderType = isBorderTypeDouble ? MeshCellBorderType.Double : MeshCellBorderType.Thin;

        let {selectedRange} = meshState.details;

        selectedRange = this._getNormalizedRange(selectedRange, meshState);

        let colFrom = selectedRange.from.colIndex;
        let rowFrom = selectedRange.from.rowIndex;
        let colTo = selectedRange.to.colIndex;
        let rowTo = selectedRange.to.rowIndex;

        const numCols = colTo - colFrom + 1;

        if( numCols < 2 ) return;

        const firstRowInRange = this._makeRange(colFrom, rowFrom, colFrom, rowTo);
        this._setBorders(ctx, firstRowInRange, [MeshCellBorderSide.Right], type, borderColor);

        const lastRowInRange = this._makeRange(colTo, rowFrom, colTo, rowTo);
        this._setBorders(ctx, lastRowInRange, [MeshCellBorderSide.Left], type, borderColor);

        if( numCols < 3 ) return;

        const centerRowsInRange = this._makeRange(colFrom + 1, rowFrom, colTo - 1, rowTo);
        this._setBorders(ctx, centerRowsInRange, [MeshCellBorderSide.Left, MeshCellBorderSide.Right], type, borderColor);
    }

    private _setAllBorders(ctx:IWSPMeshContext, resetBorder:boolean = false):void
    {
        const state = this._getState(ctx);
        if( !state ) return;

        const {meshState} = state;
        const {borderColor, isBorderTypeDouble} = rootState.ui.visualDesigner.workbench.cellSettingsBar;

        let type:MeshCellBorderType = resetBorder
            ? MeshCellBorderType.Grid
            : isBorderTypeDouble
                ? MeshCellBorderType.Double
                : MeshCellBorderType.Thin;

        let {selectedRange} = meshState.details;

        selectedRange = this._getNormalizedRange(selectedRange, meshState);

        this._setBorders(ctx, selectedRange, ALL_BORDERS, type, borderColor);

        let colFrom = selectedRange.from.colIndex;
        let rowFrom = selectedRange.from.rowIndex;
        let colTo = selectedRange.to.colIndex;
        let rowTo = selectedRange.to.rowIndex;

        let resetBordersRange;

        resetBordersRange = this._makeRange(colFrom, rowFrom - 1, colTo, rowFrom - 1);
        this._setBorders(ctx, resetBordersRange, [MeshCellBorderSide.Bottom], MeshCellBorderType.Grid);

        resetBordersRange = this._makeRange(colFrom, rowTo + 1, colTo, rowTo + 1);
        this._setBorders(ctx, resetBordersRange, [MeshCellBorderSide.Top], MeshCellBorderType.Grid);

        resetBordersRange = this._makeRange(colFrom - 1, rowFrom, colFrom - 1, rowTo);
        this._setBorders(ctx, resetBordersRange, [MeshCellBorderSide.Right], MeshCellBorderType.Grid);

        resetBordersRange = this._makeRange(colTo + 1, rowFrom, colTo + 1, rowTo);
        this._setBorders(ctx, resetBordersRange, [MeshCellBorderSide.Left], MeshCellBorderType.Grid);
    }

    private _makeRange(colFrom:number, rowFrom:number, colTo:number, rowTo:number):MeshRange
    {
        return new MeshRange(
            new MeshLocation(colFrom, rowFrom),
            new MeshLocation(colTo, rowTo)
        );
    }

    private _getNormalizedRange(range:MeshRange, meshState:MeshState):MeshRange
    {
        let colFrom = range.from.colIndex;
        let rowFrom = range.from.rowIndex;
        let colTo = range.to.colIndex;
        let rowTo = range.to.rowIndex;

        if( colFrom === null ) colFrom = 0;
        if( rowFrom === null ) rowFrom = 0;

        if( colTo === null ) colTo = meshState.size.numCols - 1;
        if( rowTo === null ) rowTo = meshState.size.numRows - 1;

        return this._makeRange(colFrom, rowFrom, colTo, rowTo);
    }

    private _setBorders(ctx:IWSPMeshContext,
                        range:MeshRange,
                        sides:MeshCellBorderSide[],
                        type:MeshCellBorderType,
                        borderColor?:MeshCellColor)
    {
        const state = this._getState(ctx);

        if( !state ) return;

        const {sheetSettingsCtrl, sheetId} = ctx;
        const {meshState} = state;

        // out of size
        if( range.from.colIndex < 0 || range.to.colIndex > meshState.size.numCols - 1 ||
            range.from.rowIndex < 0 || range.to.rowIndex > meshState.size.numRows - 1 ) return;

        // is range already normalized ?
        // range = this._getNormalizedRange(range, meshState);

        let colFrom = range.from.colIndex;
        let rowFrom = range.from.rowIndex;
        let colTo = range.to.colIndex;
        let rowTo = range.to.rowIndex;

        const numCols = colTo - colFrom + 1;
        const numRows = rowTo - rowFrom + 1;

        MeshUtils.eachCellInRange(range, numRows, numCols, (cellKey:string) =>
        {
            const cellSettings = sheetSettingsCtrl.copyCellSettings(sheetId, cellKey, cellKey);

            sides.forEach(side => cellSettings.setBorder(side, type, borderColor));
        });
    }

    private _getState(ctx:IWSPMeshContext):any
    {
        const {mesh} = commonControllers.spreadsheets;
        const {sheetSettingsCtrl, sheetId} = ctx;

        const meshState = mesh.getState(ctx);

        if( !meshState.details.selectedRange )
            return;

        const {
            details: {activeCell},
            size: {numCols, numRows}
        } = meshState;

        const activeCellSettings = sheetSettingsCtrl.getCellSettings(sheetId, activeCell.asKey());

        return {
            meshState,
            numCols,
            numRows,
            activeCellSettings
        };
    }
}
